import React, { Fragment, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { reduce } from 'lodash';

import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import {
	getCloseAccountVerificationResult,
	setCloseAccountVerificationResult,
	getCompanyDashboard,
} from 'state/reducers/customer-accounts.reducers';
import { verifyCloseAccount, closeAccount } from 'services/customer-accounts.service';
import { closeAccountTypesForms } from './forms.types.closeAccount';

import { PopupComponent } from 'components/PopupComponent';
import { ButtonComponent } from 'components/ButtonComponent';
import { DialogBox } from 'components/DialogBox';
import FormCreatorComponent from 'components/FormCreatorComponent';
import { pagesEnum } from 'constants/ui.constants';
import { useNavigate } from 'react-router-dom';

type IProps = {
	companyId: number;
};

export const CloseAccount: React.FC<IProps> = ({ companyId }: IProps) => {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { isCompanyHasPendingFinancialRequest, isCompanyHasBalance, contactsWithPermissions } =
		useAppSelector(getCloseAccountVerificationResult) || {};
	const contacts = reduce(
		contactsWithPermissions,
		(result: any[], contact) => {
			if (contact.permission == 'Signer') {
				return [...result, { label: `${contact.firstName} ${contact.lastName}`, value: contact.id.toString() }];
			}
			return result;
		},
		[]
	);

	closeAccountTypesForms.fieldsGroups[0].fields[0].props.options = contacts;

	const handleApprove = () => {
		const { contact, reason } = formState.values;
		dispatch(closeAccount(companyId, parseInt(contact), reason)).then((val) => {
			navigate('/' + pagesEnum.DASHBOARD);
		});
	};

	const [formState, setFormState] = useState<Record<string, any>>({
		dirtyFields: {},
		invalids: {},
		isDirty: false,
		isValid: false,
	});

	const [isCloseAccountFormInvalid, setIsCloseAccountFormInvalid] = useState<boolean>(true);

	return (
		<Fragment>
			{(isCompanyHasPendingFinancialRequest == undefined || isCompanyHasBalance == undefined) && <></>}

			{(isCompanyHasPendingFinancialRequest || isCompanyHasBalance) && (
				<PopupComponent
					open={true}
					title={
						isCompanyHasPendingFinancialRequest
							? t('page.customerAccounts.closeAccount.hasPending.title')
							: t('page.customerAccounts.closeAccount.hasBalance.title')
					}
					subtitle={
						isCompanyHasPendingFinancialRequest
							? t('page.customerAccounts.closeAccount.hasPending.subtitle')
							: t('page.customerAccounts.closeAccount.hasBalance.subtitle')
					}
					isDefaultButton={false}
				>
					<Stack flexDirection={'row'} gap={2}>
						<Button
							onClick={() => dispatch(setCloseAccountVerificationResult(null))}
							variant="contained"
							sx={{ borderRadius: '8px' }}
						>
							{t('button.cancel')}
						</Button>
						<Button
							onClick={() => {
								dispatch(verifyCloseAccount(companyId))
									.then((val) => navigate('/' + pagesEnum.DASHBOARD))
									.catch((error) => console.error(error));
							}}
							variant="contained"
							sx={{ borderRadius: '8px' }}
						>
							{t('button.try_again')}
						</Button>
					</Stack>
				</PopupComponent>
			)}

			{isCompanyHasPendingFinancialRequest === false && isCompanyHasBalance === false && (
				<DialogBox
					openDialog={true}
					handleClose={() => dispatch(setCloseAccountVerificationResult(null))}
					title={t('page.customerAccounts.closeAccount.closeAccountDialogue.title')}
					handleApprove={() => {
						handleApprove();
					}}
					handleReject={() => {
						dispatch(setCloseAccountVerificationResult(null));
					}}
					approveDisabled={isCloseAccountFormInvalid}
				>
					<Stack spacing={2}>
						<Stack spacing={0}>
							<Typography variant="h4" sx={{ color: 'black' }}>
								{t('page.customerAccounts.closeAccount.closeAccountDialogue.subtitle1')}
							</Typography>
							<Typography variant="h4" sx={{ color: 'black', marginBottom: '30px' }}>
								{t('page.customerAccounts.closeAccount.closeAccountDialogue.subtitle2')}
							</Typography>
						</Stack>

						<FormCreatorComponent
							{...closeAccountTypesForms}
							translation={t}
							handleChange={(formState: Record<string, any>) => {
								setFormState(formState);
								setIsCloseAccountFormInvalid(!formState.isValid);
							}}
						/>
					</Stack>
				</DialogBox>
			)}
		</Fragment>
	);
};
